import { LitElement, html, TemplateResult } from "lit"
import { customElement, property } from "lit/decorators.js"
import { ifDefined } from "lit/directives/if-defined.js"
import { ref } from "lit/directives/ref.js"
import { FocusableMixin } from "../common/mixins/FocusableMixin.js"

import componentStyle from "../common/styles/Component.css"
import itemStyle from "../dropdown-item/DropdownItem.css"
import style from "./Message.css"
import { LocalizeController } from "../localization/LocalizeController.js"

/**
 * Message represents a specific item within a collection,
 * such as notifications, tasks or conversations. Message
 * can be placed directly inside a dropdown component.
 *
 * @status ready
 * @category action
 * @slot - The message content.
 * @slot footer - Used to place content after the message. Typically used for a timestamp.
 *
 * @cssprop [--n-message-border-color=var(--n-color-border)] - Controls the border color of the message, using our [color tokens](/tokens/#color).
 *
 * @localization unreadLabel - Label for the unread messages.
 */
@customElement("nord-message")
export default class Message extends FocusableMixin(LitElement) {
  static styles = [componentStyle, itemStyle, style]

  private localize = new LocalizeController<"nord-message">(this)

  /**
   * The url the message should link to.
   */
  @property({ reflect: true }) href?: string

  /**
   * Highlight the message visually. This is meant for highlighting
   * a new message that just appeared. Highlight style should be removed
   * after the user has seen the message and there’s been a timeout of
   * e.g. 30 seconds.
   */
  @property({ reflect: true, type: Boolean }) highlight?: boolean

  /**
   * Mark the message as unread. By default messages are read.
   */
  @property({ reflect: true, type: Boolean }) unread?: boolean

  // TODO: dropdown-item and message component have the same logic
  // for determining if it renders a link vs button. Perhaps we
  // can later see if both can use a mixin or similar instead?
  render() {
    const link = (content: TemplateResult) =>
      html`<a href=${ifDefined(this.href)} ${ref(this.focusableRef)} class="n-dropdown-item n-message">${content}</a>`
    const button = (content: TemplateResult) =>
      html`<button ${ref(this.focusableRef)} class="n-dropdown-item n-message">${content}</button>`

    const container = this.href ? link : button

    return container(html`
      <div role="img" class="n-unread" aria-label=${this.localize.term("unreadLabel")} ?hidden=${!this.unread}></div>
      <slot></slot>
      <slot name="footer"></slot>
    `)
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "nord-message": Message
  }
}
